// 
// ─── IMPORTING NECESSARY ELEMENTS ───────────────────────────────────────
//
import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Cookies from 'universal-cookie';
import Api from '../contexts/Api';
import { getTranslate, prepareFormData, getValue } from '../contexts/Utils';
import { useNotification } from '../components/UseNotification';
import Formfields from '../components/Formfields';
import SwitchTheme from '../components/SwitchTheme';

// 
// ─── COMPONENT DECLARATION ───────────────────────────────────────
//
const Login = () => {
  let lang = 'fr';
  const { notification } = useNotification()
  const cookies = new Cookies();
  const userData = cookies.get('nudge_user');
  const [errors, setErrors] = useState({});

  // 
  // ─── HANDLE USER LOGIN ───────────────────────────────────────
  //
  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = prepareFormData({ formId: 'loginForm' });
    if (data.errors) return setErrors(data.errors);

    const params = {
      data,
      endpoint: '/auth/login'
    }
    const logUser = await Api(params)

    // 
    // ─── ON SUCCESS WE USE REDUX TO LOG USER ───────────────────────────────────────
    //
    if (logUser.success && logUser.access_token) {
      cookies.set('nudge_user', logUser.data, {
        expires: new Date(Date.now() + 604800000), // time until expiration
        secure: false, // true if using https
      })
      return window.location = '/';
    } else {
      notification({ variant: 'error', message: logUser.message })
    }
  }

  // 
  // ─── FORM FIELDS ───────────────────────────────────────
  //
  const formFields = [
    { shrink: false, type: "email", component: "text", name: "email", label: "Email", required: true, error: getValue(errors, ['email', 'message']) },
    { checkPassword: false, shrink: false, type: "password", component: "text", name: "password", label: getTranslate('password', lang), required: true, error: getValue(errors, ['password', 'message']) },
  ]

  // 
  // ─── COMPONENT RENDER ───────────────────────────────────────
  //
  return userData ? <Navigate to="/" /> : <S.Container>
    <S.Content>
      <S.Header>
        <svg xmlns="http://www.w3.org/2000/svg" width="396" height="102" viewBox="0 0 396 102">
          <text id="NUDGE_TECH" data-name="NUDGE TECH" transform="translate(0 84)" fill="#004e91" fontSize="83" fontFamily="Impact"><tspan x="0" y="0">NUDGE</tspan><tspan y="0" xmlSpace="preserve" fill="#86b2d8"> TECH</tspan></text>
        </svg>
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" width="122.739" height="114.537" viewBox="0 0 122.739 114.537">
            <g id="Fonf_projet" data-name="Fonf projet" transform="translate(-655.761 -1733.389)">
              <path id="Tracé_34" data-name="Tracé 34" d="M40.9,6.95c22.591,0,40.9-18.23,40.9,4.361a40.9,40.9,0,1,1-81.81,0C0-11.28,18.314,6.95,40.9,6.95Z" transform="matrix(-0.574, 0.819, -0.819, -0.574, 778.5, 1763.339)" opacity="0.7" style={{ mixBlendMode: 'multiply', isolation: 'isolate' }} />
              <path id="Tracé_35" data-name="Tracé 35" d="M40.9,6.95c22.591,0,40.9-18.23,40.9,4.361a40.9,40.9,0,0,1-81.81,0C0-11.28,18.314,6.95,40.9,6.95Z" transform="matrix(0.438, 0.899, -0.899, 0.438, 702.692, 1751.507)" opacity="0.33" style={{ mixBlendMode: 'multiply', isolation: 'isolate' }} />
            </g>
          </svg>
          <span>{getTranslate('connexion2', lang)}</span>
        </div>
      </S.Header>
      <S.Form name='loginForm'>
        {formFields.map((el) => (<Formfields key={el.name} field={el} />))}
        <Link to='/mot-de-passe-oublie'>{getTranslate('forgot_your_password', lang)}</Link>
        <S.Button onClick={handleSubmit}>{getTranslate('connexion', lang)}</S.Button>
      </S.Form>
      <S.Footer>
        <p>{getTranslate('no_account', lang)} <br /> <Link to='/inscription'>{getTranslate('join_us', lang)}</Link></p>
        <S.LinksContainer>
          <S.Link to='/cgu'>CGU</S.Link>
          <S.Link to='/cgv'>CGV</S.Link>
          <S.Link to='/mentions-legales'>Mentions Légales</S.Link>
          <S.Link as='a' href="https://nudgetech.fr/" target="_blank" rel="noopener noreferrer">Site internet</S.Link>
        </S.LinksContainer>
      </S.Footer>
    </S.Content>
    <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
      <path data-name="Icône logo Blanc" d="M1309.634,1526.167c5.718,3.83,12.676,3.452,18.393.324a22.381,22.381,0,0,0,10.68-13.484,21.564,21.564,0,0,1-2.859,5.663,20.566,20.566,0,0,1-3.884,4.207c-.431.323-.809.594-1.24.917-.432.269-.809.539-1.241.809a16.336,16.336,0,0,1-9.169,2.373,11.7,11.7,0,0,1-6.311-2.049l-.324-.162a6.878,6.878,0,0,1-.917-.862,14.342,14.342,0,0,0,8.522.808,16.218,16.218,0,0,0,9.385-5.663,19.624,19.624,0,0,0,4.477-9.924,22.818,22.818,0,0,0-2.589-14.24c3.129,6.311,3.4,13.97-.863,20.226-2.319,3.344-5.771,5.879-9.816,5.771a14.177,14.177,0,0,0,7.012-6.041,16.653,16.653,0,0,0,2.265-7.443,19.207,19.207,0,0,0-3.344-11.974,22.138,22.138,0,0,0-12.46-9.008c8.253,2.643,15.1,10.787,13.808,19.9a10.378,10.378,0,0,1-2.967,6.04,14.436,14.436,0,0,0,.486-9.978,17.75,17.75,0,0,0-8.145-9.978,21.963,21.963,0,0,0-21.359.162,21.822,21.822,0,0,1,6.9-2.1,22.774,22.774,0,0,1,3.291-.162,20.446,20.446,0,0,1,5.933,1.025,18.517,18.517,0,0,1,3.614,1.726,14.265,14.265,0,0,1,4.909,5.071,13.421,13.421,0,0,1,.916,2,7.767,7.767,0,0,1,.485,3.129,14.161,14.161,0,0,0-5.447-6.634,16.667,16.667,0,0,0-12.351-2.427,19.833,19.833,0,0,0-8.469,3.667,23.227,23.227,0,0,0-8.252,11.92c2.211-6.472,7.281-11.974,14.293-13.592,4.1-.917,8.684-.323,11.651,2.7a14.6,14.6,0,0,0-10.68-.216,16.864,16.864,0,0,0-4.1,2.211,19.043,19.043,0,0,0-7.714,13.754,22.682,22.682,0,0,0,2.751,13.269c-3.29-6.472-3.4-14.563,1.187-20.712,2.319-3.074,5.609-5.34,9.438-5.286a13.9,13.9,0,0,0-5.286,3.722,16.6,16.6,0,0,0-3.56,14.94,20.6,20.6,0,0,0,6.9,11.273,24.888,24.888,0,0,0,8.468,4.585,21.749,21.749,0,0,1-11.165-8.63c-3.02-4.746-4.045-10.572-1.133-15.533a3.686,3.686,0,0,0,.378-.594,5.951,5.951,0,0,1,1.078-1.24c-.216.593-.485,1.186-.647,1.78-.108.432-.162.809-.27,1.24-1.348,7.335,3.075,14.185,9.439,17.367a22.036,22.036,0,0,0,20.5-.593,22.977,22.977,0,0,1-6.85,2.1,22.139,22.139,0,0,1-5.663,0,18.733,18.733,0,0,1-6.1-1.942,15.085,15.085,0,0,1-4.962-4.1c-.216-.27-.378-.539-.593-.809a9.953,9.953,0,0,1-1.888-5.933A14.43,14.43,0,0,0,1309.634,1526.167Zm-5.755-15.4a9.129,9.129,0,0,1,3.244-4.687,7.8,7.8,0,0,1,3.329-1.442,6.556,6.556,0,0,1,4.856.955,5.571,5.571,0,0,1,2.142,2.608,3.058,3.058,0,0,0-.191-1.23,5.358,5.358,0,0,0-.36-.785,5.615,5.615,0,0,0-1.93-1.993,7.284,7.284,0,0,0-1.42-.678,8.081,8.081,0,0,0-2.332-.4,9.02,9.02,0,0,0-1.294.064,8.585,8.585,0,0,0-2.714.827,8.634,8.634,0,0,1,8.4-.063,6.975,6.975,0,0,1,3.2,3.923,5.673,5.673,0,0,1-.191,3.923,4.082,4.082,0,0,0,1.166-2.375c.509-3.583-2.184-6.786-5.428-7.825a8.706,8.706,0,0,1,4.9,3.541,7.546,7.546,0,0,1,1.314,4.708,6.543,6.543,0,0,1-.89,2.926,5.57,5.57,0,0,1-2.757,2.374,4.693,4.693,0,0,0,3.859-2.268,7.815,7.815,0,0,0,.339-7.952,8.969,8.969,0,0,1,1.018,5.6,7.714,7.714,0,0,1-1.76,3.9,6.377,6.377,0,0,1-3.689,2.226,5.634,5.634,0,0,1-3.35-.318,2.749,2.749,0,0,0,.361.34l.127.063a4.6,4.6,0,0,0,2.481.805,6.423,6.423,0,0,0,3.6-.933c.169-.106.318-.212.487-.318.169-.128.319-.234.488-.361a8.1,8.1,0,0,0,1.527-1.654,8.439,8.439,0,0,0,1.124-2.227,8.793,8.793,0,0,1-4.2,5.3,6.887,6.887,0,0,1-7.231-.127,5.672,5.672,0,0,1-2.141-2.609,3.912,3.912,0,0,0,.742,2.333c.085.106.148.212.233.318a5.923,5.923,0,0,0,1.951,1.611,7.343,7.343,0,0,0,2.4.763,8.642,8.642,0,0,0,2.226,0,9.04,9.04,0,0,0,2.693-.826,8.663,8.663,0,0,1-8.058.233,6.49,6.49,0,0,1-3.711-6.827c.042-.17.064-.318.106-.488.064-.233.17-.466.255-.7a2.315,2.315,0,0,0-.424.488,1.433,1.433,0,0,1-.148.233,5.8,5.8,0,0,0,.445,6.107,8.558,8.558,0,0,0,4.389,3.393,9.8,9.8,0,0,1-3.328-1.8,8.1,8.1,0,0,1-2.715-4.432,6.525,6.525,0,0,1,1.4-5.874,5.463,5.463,0,0,1,2.078-1.463,4.676,4.676,0,0,0-3.71,2.078,7.736,7.736,0,0,0-.467,8.143,8.92,8.92,0,0,1-1.082-5.216,7.484,7.484,0,0,1,3.033-5.407,6.622,6.622,0,0,1,1.611-.869,5.74,5.74,0,0,1,4.2.084,4.881,4.881,0,0,0-4.58-1.06A7.971,7.971,0,0,0,1303.879,1510.769Z" transform="translate(-1288.707 -1486.416)" />
    </svg>
    <SwitchTheme />
  </S.Container>
};

export default Login;

// 
// ───  STYLE DEFINITION ───────────────────────────────────────
//
const S = {}
const rotate = keyframes`
  from {
    transform: scale(21) rotate(0deg);
  }

  to {
    transform: scale(21) rotate(360deg);
  }
`;

S.Container = styled.div`
  min-height: 100vh;
  width: 100vw;
  overflow: hidden;
  background: ${({ theme }) => theme.card};
  padding: 20px 0;
  box-sizing: border-box;
  position: relative;

  // right big logo
  & > svg {
    z-index: 0;
    position: absolute;
    right: 20%;
    top: 50%;
    fill: ${({ theme }) => theme.blue};
    transform: scale(21);
    animation: ${rotate} 150s linear infinite;

    @media (max-width: 1400px) {
      fill: ${({ theme }) => theme.lightBlue};
    }
  }
`

S.Content = styled.div`
  z-index: 10;
  max-width: 600px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  justify-content: space-between;
`

S.Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 35px;

  // nudge tech svg
  & > svg {
    width: 230px;
  }

  // connexion + svg
  & div {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    font-weight: bold;
    font-size: 16px;

    & svg {
      position: absolute;
      width: 60px;
      z-index: -1;
      fill: ${({ theme }) => theme.blue};
    }
  }
`

S.Form = styled.form`
  position: absolute;
  top: 50%;
  transform: translateY(-30%);
  width: 370px;
  box-sizing: border-box;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  & *:not(.Mui-error, button) {
    color: ${({ theme }) => theme.text} !important;
    border-color: ${({ theme }) => theme.text} !important;
  }
  & label.Mui-error {
    color: #d32f2f !important;
  }
  & div.Mui-error > input, div.Mui-error > textarea {
    border-color: #d32f2f !important;
  }

  // forgot password
  & > a {
    font-size: 13px;
    font-weight: bold;
    align-self: flex-end;
    margin-top: 5px;
  }

  @media (min-height: 800px) {
    transform: translateY(-50%);
  }
  @media (max-width: 450px) {
    width: 100%;
  }
`

S.Button = styled.button`
  background: ${({ theme }) => theme.linear};
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  padding: 12px 28px;
  border-radius: 5px;
  width: fit-content;
  align-self: center;
  margin-top: 35px;
  cursor: pointer;
`

S.Footer = styled.div`
  font-size: 14px;
  padding: 20px 0;
  box-sizing: border-box;
  width: 100%;
  text-align: center;

  & p > a {
    font-weight: bold;
    background-image: ${({ theme }) => theme.linear};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent; 
    background-clip: text;
    -moz-text-fill-color: transparent;
  }
`

S.Link = styled(Link)`
  font-size:0.8em;
  color:${({ theme }) => theme.blue} !important;
`

S.LinksContainer = styled.div`
  display:flex;
  justify-content:center;
  gap:10px;
`